/**
 * Preeclampsia-Focused Prenatal Training Plan
 *
 * Weeks 4 → Delivery (modeled as 37 weeks total: pregnancy week 4 through 40)
 *
 * IMPORTANT:
 * - No periodization engine (usesPeriodization=false)
 * - Intensity is RPE 5–6 across trimesters (conversational pace)
 * - No breath holding / no training to failure
 * - If preeclampsia or severe HTN is diagnosed: provider-directed only
 *
 * Mapping:
 * - Program Week 1 = Pregnancy Week 4
 * - Program Week 10 = Pregnancy Week 13
 * - Program Week 11 = Pregnancy Week 14
 * - Program Week 24 = Pregnancy Week 27
 * - Program Week 25 = Pregnancy Week 28
 * - Program Week 37 = Pregnancy Week 40
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'prenatal-preeclampsia-focused',
  name: 'Prenatal BP Support (Preeclampsia-Focused)',
  description:
    'A trimester-aware, preeclampsia-focused prenatal plan emphasizing blood pressure stability, circulation, and adherence. 4 training days/week + 2 step days + optional recovery day. RPE 5–6, no breath holding, no failure. Cleared by OB required.',
  level: 'recruit',
  experienceLevel: 'intermediate',
  duration: 37, // pregnancy week 4 → week 40
  daysPerWeek: 7,
  difficulty: 'beginner',
  goals: ['prenatal', 'blood-pressure', 'endurance', 'strength-maintenance'],
  tags: ['prenatal', 'preeclampsia', 'bp', 'low-risk-intensity', 'walking', 'mobility'],
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: false, // Explicitly disable periodization engine
  requiredEquipment: 'minimal', // Bodyweight, bands, light dumbbells
  restrictedToGender: 'female', // Prenatal program - women only
};

// ------------------------------
// Shared blocks/helpers
// ------------------------------

const SAFETY_BLOCK = (trimesterLabel: string): any => ({
  name: 'Safety Rules (Read)',
  type: 'prep',
  format: 'instructions',
  exercises: [
    {
      name: `Prenatal Safety — ${trimesterLabel}`,
      sets: 1,
      reps: 'Read',
      format: 'instructions',
      details:
        'Target RPE 5–6 (you can talk in full sentences). No breath holding (no Valsalva). No training to failure. Program Week 1 = Pregnancy Week 4. Stop if you feel dizzy, chest pain, bleeding, severe headache, or unusual symptoms; contact your OB. If preeclampsia or severe HTN is diagnosed: exercise becomes provider-directed only.',
    },
  ],
});

const WORK_DAY_TEMPLATE = (label: 'Monday' | 'Wednesday'): WorkoutTemplateDay => ({
  name: `${label} — Work Day (Steps Only)`,
  sessionType: 'pt',
  estimatedDuration: 5,
  blocks: [
    {
      name: 'Work Day',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'Work Day — Steps Only',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details:
            'No formal workout today. Steps at work count as aerobic activity (~20–30 min equivalent). Optional: 5–10 min breathing or gentle mobility only.',
        },
      ],
    },
  ],
});

const OPTIONAL_SUNDAY_TEMPLATE: WorkoutTemplateDay = {
  name: 'Sunday — Optional Recovery Walk / Rest',
  sessionType: 'pt',
  estimatedDuration: 25,
  blocks: [
    {
      name: 'Recovery',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'Optional Recovery Walk',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details:
            'Optional: 20–30 min easy walk at conversational pace (RPE 4–5), or full rest. Focus on hydration and sleep.',
        },
      ],
    },
  ],
};

// ------------------------------
// Trimester 1 (Program weeks 1–10)
// ------------------------------

const T1_DAY2: WorkoutTemplateDay = {
  name: 'Tuesday — Strength A + Short Cardio (T1)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 1 (Weeks 4–13)'),
    {
      name: 'Strength A (RPE 5–6)',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Box squat is ok if preferred. Exhale through effort.' },
        { name: 'Romanian Deadlift', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Use dumbbells if available. Keep spine neutral; stop well short of strain.' },
        { name: 'Dumbbell Row', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: '1-arm supported row is ideal. Focus on posture.' },
        { name: 'Incline Dumbbell Press', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Incline or supported pressing only.' },
        { name: 'Step-Ups', sets: 2, reps: '10-12', intensity: 'RPE 5-6', rest: '60-90s', details: 'Use a low box; hold support if needed.' },
        { name: 'Pallof Press', sets: 2, reps: '10-12', intensity: 'RPE 5-6', rest: '45-60s', details: 'Anti-rotation core. No breath holding.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 5-6', details: 'Walk or bike at conversational pace.' },
      ],
    },
    {
      name: 'Auto-Adjustments',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'If nausea/fatigue is high…',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details: 'Replace strength with a 20–30 min easy walk. Keep RPE 4–5. No “make-up workouts.”',
        },
      ],
    },
  ],
};

const T1_DAY4: WorkoutTemplateDay = {
  name: 'Thursday — Cardio + Mobility (T1)',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    SAFETY_BLOCK('Trimester 1 (Weeks 4–13)'),
    {
      name: 'Cardio (Steady)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '25-30 min', intensity: 'RPE 5-6', details: 'Walk, bike, or pool. Conversational pace.' },
      ],
    },
    {
      name: 'Mobility / Prenatal Yoga',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'Mobility Flow (15–20 min)',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details: 'Gentle prenatal mobility/yoga. Emphasize hips, T-spine, breathing. Stop if uncomfortable.',
        },
      ],
    },
  ],
};

const T1_DAY5: WorkoutTemplateDay = {
  name: 'Friday — Strength B + Short Cardio (T1)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 1 (Weeks 4–13)'),
    {
      name: 'Strength B (RPE 5–6)',
      type: 'strength',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 2, reps: '8-12', intensity: 'RPE 5-6', rest: '60-90s', details: 'Supported split squat is fine. Shorten stride if needed.' },
        { name: 'Hip Thrust', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Glute bridge if preferred.' },
        { name: 'Lat Pulldown', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Band pulldown is ok. Posture + breathing.' },
        { name: 'Seated Dumbbell Press', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Seated only. Stop well short of strain.' },
        { name: "Farmer's Carry", sets: 3, reps: '40m', intensity: 'RPE 5-6', rest: '45-60s', details: 'Light carry. Tall posture, steady breathing.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 5-6', details: 'Walk or bike, conversational pace.' },
      ],
    },
  ],
};

const T1_DAY6: WorkoutTemplateDay = {
  name: 'Saturday — Long Easy Cardio (T1)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 1 (Weeks 4–13)'),
    {
      name: 'Cardio (Long, Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '35-45 min', intensity: 'RPE 5-6', details: 'Outdoor walking encouraged. Conversational pace.' },
      ],
    },
  ],
};

// ------------------------------
// Trimester 2 (Program weeks 11–24)
// ------------------------------

const T2_DAY2: WorkoutTemplateDay = {
  name: 'Tuesday — Strength A + Short Cardio (T2)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 2 (Weeks 14–27)'),
    {
      name: 'Strength A (RPE 5–6)',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Box squat pattern. No prolonged flat-on-back work.' },
        { name: 'Romanian Deadlift', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Slightly reduce loading; perfect form.' },
        { name: 'Seated Cable Row', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Rows emphasized for posture.' },
        { name: 'Incline Dumbbell Press', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Incline or seated pressing only.' },
        { name: 'Hip Abduction', sets: 2, reps: '12-15', intensity: 'RPE 5-6', rest: '45-60s', details: 'Band/standing hip abduction. Pelvic support.' },
        { name: 'Pallof Press', sets: 2, reps: '10-12', intensity: 'RPE 5-6', rest: '45-60s', details: 'Anti-rotation core, steady breathing.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 5-6', details: 'Conversational pace. Upright posture.' },
      ],
    },
    {
      name: 'Auto-Adjustments',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'If pelvic pressure or heartburn…',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details:
            'Pelvic pressure: remove single-leg loading or shorten stride. Heartburn: avoid deep bending; stay upright. Keep RPE 4–6.',
        },
      ],
    },
  ],
};

const T2_DAY4: WorkoutTemplateDay = {
  name: 'Thursday — Cardio + Mobility (T2)',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    SAFETY_BLOCK('Trimester 2 (Weeks 14–27)'),
    {
      name: 'Cardio (Steady)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '25-30 min', intensity: 'RPE 5-6', details: 'Steady, conversational. Walk, bike, or pool.' },
      ],
    },
    {
      name: 'Mobility / Prenatal Yoga',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'Mobility Flow (15–20 min)',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details: 'Gentle mobility. Avoid prolonged supine positions. Focus on breathing + posture.',
        },
      ],
    },
  ],
};

const T2_DAY5: WorkoutTemplateDay = {
  name: 'Friday — Strength B + Short Cardio (T2)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 2 (Weeks 14–27)'),
    {
      name: 'Strength B (RPE 5–6)',
      type: 'strength',
      exercises: [
        { name: 'Step-Ups', sets: 2, reps: '8-12', intensity: 'RPE 5-6', rest: '60-90s', details: 'Lower step height; use support.' },
        { name: 'Hip Thrust', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Glute emphasis.' },
        { name: 'Lat Pulldown', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Posture + breathing.' },
        { name: 'Seated Dumbbell Press', sets: 2, reps: '10-15', intensity: 'RPE 5-6', rest: '60-90s', details: 'Seated only. Avoid strain.' },
        { name: 'Suitcase Carry', sets: 3, reps: '40m', intensity: 'RPE 5-6', rest: '45-60s', details: 'Light carry, swap sides. Tall posture.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 5-6', details: 'Conversational pace.' },
      ],
    },
  ],
};

const T2_DAY6: WorkoutTemplateDay = {
  name: 'Saturday — Long Easy Cardio (T2)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 2 (Weeks 14–27)'),
    {
      name: 'Cardio (Long, Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '35-45 min', intensity: 'RPE 5-6', details: 'Conversational pace. Can be bike/pool if preferred.' },
      ],
    },
  ],
};

// ------------------------------
// Trimester 3 (Program weeks 25–37)
// ------------------------------

const T3_DAY2: WorkoutTemplateDay = {
  name: 'Tuesday — Minimal Effective Strength + Short Cardio (T3)',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    SAFETY_BLOCK('Trimester 3 (Weeks 28–Delivery)'),
    {
      name: 'Minimal Strength (RPE 4–6)',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 1, reps: '8-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Sit-to-stand pattern. Stop if uncomfortable.' },
        { name: 'Seated Cable Row', sets: 1, reps: '10-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Posture + breathing.' },
        { name: 'Romanian Deadlift', sets: 1, reps: '8-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Supported hip hinge (light). No strain.' },
        { name: 'Push-Ups', sets: 1, reps: '8-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Wall or incline push-ups. No breath holding.' },
        { name: 'Hip Abduction', sets: 1, reps: '12-15', intensity: 'RPE 4-6', rest: '45-60s', details: 'Band/standing abduction. Pelvic support.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 4-6', details: 'Conversational pace.' },
      ],
    },
    {
      name: 'Breathing + Pelvic Floor Coordination',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        { name: 'Breathing + Pelvic Floor', sets: 1, reps: 'Read', format: 'instructions', details: '5 minutes. Slow exhales. Gentle pelvic floor coordination. Stop if dizzy.' },
      ],
    },
  ],
};

const T3_DAY4: WorkoutTemplateDay = {
  name: 'Thursday — Cardio + Yoga/Breath (T3)',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    SAFETY_BLOCK('Trimester 3 (Weeks 28–Delivery)'),
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '20-25 min', intensity: 'RPE 4-6', details: 'Keep it comfortable and steady.' },
      ],
    },
    {
      name: 'Yoga / Breathwork',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'Yoga/Breathwork (20 min)',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details: 'Gentle prenatal mobility + breathing. Avoid prolonged supine positions. Stop if uncomfortable.',
        },
      ],
    },
  ],
};

const T3_DAY5: WorkoutTemplateDay = {
  name: 'Friday — Minimal Effective Strength + Short Cardio (T3)',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    SAFETY_BLOCK('Trimester 3 (Weeks 28–Delivery)'),
    {
      name: 'Minimal Strength (RPE 4–6)',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 1, reps: '8-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Sit-to-stand pattern.' },
        { name: 'Lat Pulldown', sets: 1, reps: '10-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Band pulldown ok.' },
        { name: 'Hip Thrust', sets: 1, reps: '10-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Glute bridge if more comfortable.' },
        { name: 'Push-Ups', sets: 1, reps: '8-12', intensity: 'RPE 4-6', rest: '60-90s', details: 'Wall or incline. No strain.' },
      ],
    },
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '10-15 min', intensity: 'RPE 4-6', details: 'Conversational pace.' },
      ],
    },
    {
      name: 'Breathing',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        { name: 'Breathing + Pelvic Floor', sets: 1, reps: 'Read', format: 'instructions', details: '5 minutes. Slow exhales. Gentle pelvic floor coordination.' },
      ],
    },
    {
      name: 'Auto-Adjustments',
      type: 'recovery',
      format: 'instructions',
      exercises: [
        {
          name: 'If sleep is poor or swelling/BP is rising…',
          sets: 1,
          reps: 'Read',
          format: 'instructions',
          details: 'Drop Friday strength for the week and walk only. Keep RPE 4–5 and notify OB for concerning symptoms.',
        },
      ],
    },
  ],
};

const T3_DAY6: WorkoutTemplateDay = {
  name: 'Saturday — Easy Cardio (T3)',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    SAFETY_BLOCK('Trimester 3 (Weeks 28–Delivery)'),
    {
      name: 'Cardio (Easy)',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Walk', duration: '20-35 min', intensity: 'RPE 4-6', details: 'Can split into 2 shorter walks if fatigued.' },
      ],
    },
  ],
};

// Program export with template ranges
export const preeclampsiaPrenatalProgram: WorkoutProgram = {
  metadata,
  workouts: [], // Using templates only
  templates: {
    // Trimester 1 (program weeks 1–10)
    'week1-10-day1': WORK_DAY_TEMPLATE('Monday'),
    'week1-10-day2': T1_DAY2,
    'week1-10-day3': WORK_DAY_TEMPLATE('Wednesday'),
    'week1-10-day4': T1_DAY4,
    'week1-10-day5': T1_DAY5,
    'week1-10-day6': T1_DAY6,
    'week1-10-day7': OPTIONAL_SUNDAY_TEMPLATE,

    // Trimester 2 (program weeks 11–24)
    'week11-24-day1': WORK_DAY_TEMPLATE('Monday'),
    'week11-24-day2': T2_DAY2,
    'week11-24-day3': WORK_DAY_TEMPLATE('Wednesday'),
    'week11-24-day4': T2_DAY4,
    'week11-24-day5': T2_DAY5,
    'week11-24-day6': T2_DAY6,
    'week11-24-day7': OPTIONAL_SUNDAY_TEMPLATE,

    // Trimester 3 (program weeks 25–37)
    'week25-37-day1': WORK_DAY_TEMPLATE('Monday'),
    'week25-37-day2': T3_DAY2,
    'week25-37-day3': WORK_DAY_TEMPLATE('Wednesday'),
    'week25-37-day4': T3_DAY4,
    'week25-37-day5': T3_DAY5,
    'week25-37-day6': T3_DAY6,
    'week25-37-day7': OPTIONAL_SUNDAY_TEMPLATE,
  },
};


